import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bar, Pie } from "react-chartjs-2";
import GrafikVerifikasi from "./GrafikVerifikasi";
import GrafikRegisterUsulan from "./GrafikRegisterUsulan";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { 
  Box,
  Container, 
  Paper, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { 
  Activity as ActivityIcon,
  Home as HomeIcon, 
  Users as UsersIcon, 
  UserMinus as UserMinusIcon,
  PieChart as ChartIcon,
  BarChart as BarChartIcon,
  Map as MapIcon
} from "lucide-react";

ChartJS.register(ChartDataLabels);
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

export const GrafikSeluruhKecamatan = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('bar');
  const [dataAll, setDataAll] = useState({
    overall: null,
    kecamatan: [],
    backlog: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_URL}/dataall`, { withCredentials: true });
        
        if (response.data && response.data.code === 200) {
          const overallData = response.data.dataTotal.find(item => item.statType === "overall");
          const kecamatanData = response.data.dataTotal.filter(item => item.statType === "kecamatan");
          const backlogData = response.data.dataTotal.filter(item => item.statType === "backlog");
          
          setDataAll({
            overall: overallData,
            kecamatan: kecamatanData,
            backlog: backlogData
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  // Create chart data for overall statistics
  const overallChartData = {
    labels: [""],
    datasets: [
      {
        label: "Rumah Layak Huni",
        data: [dataAll.overall?.layakHuni || 0],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: [dataAll.overall?.tidakLayakHuni || 0],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
      {
        label: "Berpenghuni",
        data: [dataAll.overall?.berpenghuni || 0],
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: "Tidak Berpenghuni",
        data: [dataAll.overall?.tidakBerpenghuni || 0],
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
      {
        label: "Total Backlog",
        data: [dataAll.overall?.backlog || 0],
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  // Create chart data for per-kecamatan statistics
  const kecamatanChartData = {
    labels: dataAll.kecamatan.map(item => item.category),
    datasets: [
      {
        label: "Rumah Layak Huni",
        data: dataAll.kecamatan.map(item => item.layakHuni),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: dataAll.kecamatan.map(item => item.tidakLayakHuni),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
      {
        label: "Berpenghuni",
        data: dataAll.kecamatan.map(item => item.berpenghuni),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: "Tidak Berpenghuni",
        data: dataAll.kecamatan.map(item => item.tidakBerpenghuni),
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
      {
        label: "Backlog",
        data: dataAll.kecamatan.map(item => item.backlog),
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isExtraSmall ? "bottom" : "top",
        labels: {
          font: { size: isMobile ? 10 : 14 },
          boxWidth: isMobile ? 10 : 18,
          padding: isMobile ? 5 : 10
        },
      },
      title: {
        display: true,
        text: "Statistik Rumah per Kecamatan",
        font: { size: isMobile ? 16 : 20, weight: 'bold' },
        padding: { bottom: 20 }
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        font: { size: isExtraSmall ? 6 : (isMobile ? 8 : 11), weight: 'bold' },
        color: '#444',
        formatter: (value) => value > 0 ? value : '',
        offset: 0,
        padding: 0,
        display: true
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: isMobile ? 10 : 14 },
        bodyFont: { size: isMobile ? 10 : 14 },
        padding: 10
      }
    },
    scales: {
      x: {
        ticks: { 
          font: { size: isExtraSmall ? 6 : (isMobile ? 8 : 12) },
          maxRotation: isExtraSmall ? 90 : 45,
          minRotation: isExtraSmall ? 90 : 45
        },
        grid: {
          display: false
        }
      },
      y: {
        ticks: { font: { size: isExtraSmall ? 6 : (isMobile ? 8 : 12) } },
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
    },
    barPercentage: isExtraSmall ? 0.6 : 0.8,
    categoryPercentage: isExtraSmall ? 0.7 : 0.9,
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isExtraSmall ? "bottom" : "right",
        labels: {
          font: { size: isMobile ? 10 : 14 },
          boxWidth: isMobile ? 10 : 18,
          padding: isMobile ? 5 : 10
        },
      },
      title: {
        display: true,
        text: "Total Statistik Rumah Seluruh Kecamatan",
        font: { size: isMobile ? 16 : 20, weight: 'bold' },
        padding: { bottom: 10 }
      },
      datalabels: {
        formatter: (value, ctx) => {
          if (value < 10) return '';
          return `${value}\n(${(value / ctx.dataset.data.reduce((a, b) => a + b, 0) * 100).toFixed(1)}%)`;
        },
        color: '#fff',
        font: { size: isExtraSmall ? 8 : (isMobile ? 10 : 12), weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  // Creating statistics summary cards
  const statCards = [
    { 
      title: "Rumah Layak Huni", 
      value: dataAll.overall?.layakHuni || 0, 
      icon: <HomeIcon size={isMobile ? 20 : 24} color="#4BC0C0" />,
      color: "#4BC0C0"
    },
    { 
      title: "Rumah Tidak Layak Huni", 
      value: dataAll.overall?.tidakLayakHuni || 0, 
      icon: <HomeIcon size={isMobile ? 20 : 24} color="#FF6384" />,
      color: "#FF6384"
    },
    { 
      title: "Total Rumah", 
      value: (dataAll.overall?.layakHuni || 0) + (dataAll.overall?.tidakLayakHuni || 0), 
      icon: <ActivityIcon size={isMobile ? 20 : 24} color="#008000" />,
      color: "#008000"
    },
    { 
      title: "Berpenghuni", 
      value: dataAll.overall?.berpenghuni || 0, 
      icon: <UsersIcon size={isMobile ? 20 : 24} color="#36A2EB" />,
      color: "#36A2EB"
    },
    { 
      title: "Tidak Berpenghuni", 
      value: dataAll.overall?.tidakBerpenghuni || 0, 
      icon: <UserMinusIcon size={isMobile ? 20 : 24} color="#FFCE56" />,
      color: "#FFCE56"
    },
    { 
      title: "Total Backlog", 
      value: dataAll.overall?.backlog || 0, 
      icon: <ChartIcon size={isMobile ? 20 : 24} color="#9966FF" />,
      color: "#9966FF"
    }
  ];

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
          Mengambil data statistik...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 4 }, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)'
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            textAlign: 'center',
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
            mb: { xs: 2, md: 4 },
            color: '#2a3f5f'
          }}
        >
          Statistik Perumahan Seluruh Kecamatan
        </Typography>

        {/* Summary Statistics Cards */}
        <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: { xs: 3, md: 5 } }}>
          {statCards.map((card, index) => (
            <Grid item xs={6} sm={4} md={4} lg={2} key={index}>
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6
                  },
                  border: `1px solid ${card.color}30`
                }}
              >
                <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    alignItems="center" 
                    sx={{ mb: 0.5 }}
                  >
                    {card.icon}
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Stack>
                  <Typography 
                    variant="h5" 
                    component="div" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: card.color,
                      fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.5rem' }
                    }}
                  >
                    {card.value.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: { xs: 3, md: 5 } }} />

        {/* Toggle for chart type */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            aria-label="chart type"
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="bar" aria-label="bar chart">
              <BarChartIcon size={isMobile ? 16 : 20} />
              <Typography sx={{ ml: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Bar</Typography>
            </ToggleButton>
            <ToggleButton value="pie" aria-label="pie chart">
              <ChartIcon size={isMobile ? 16 : 20} />
              <Typography sx={{ ml: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Pie</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Overall Statistics Chart */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            mb: { xs: 3, md: 5 }, 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ height: { xs: 300, sm: 350, md: 400 } }}>
            {chartType === 'bar' ? (
              <Bar data={overallChartData} options={{ 
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    ...chartOptions.plugins.title,
                    text: "Statistik Keseluruhan"
                  }
                }
              }} />
            ) : (
              <Pie data={{
                labels: [
                  "Rumah Layak Huni", 
                  "Rumah Tidak Layak Huni", 
                  "Berpenghuni", 
                  "Tidak Berpenghuni", 
                  "Total Backlog"
                ],
                datasets: [{
                  data: [
                    dataAll.overall?.layakHuni || 0,
                    dataAll.overall?.tidakLayakHuni || 0,
                    dataAll.overall?.berpenghuni || 0,
                    dataAll.overall?.tidakBerpenghuni || 0,
                    dataAll.overall?.backlog || 0
                  ],
                  backgroundColor: [
                    "rgba(75, 192, 192, 0.6)",
                    "rgba(255, 99, 132, 0.6)",
                    "rgba(54, 162, 235, 0.6)",
                    "rgba(255, 206, 86, 0.6)",
                    "rgba(153, 102, 255, 0.6)"
                  ],
                  borderColor: [
                    "rgba(75, 192, 192, 1)",
                    "rgba(255, 99, 132, 1)",
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 206, 86, 1)",
                    "rgba(153, 102, 255, 1)"
                  ],
                  borderWidth: 1
                }]
              }} options={pieOptions} />
            )}
          </Box>
        </Paper>

        {/* Per-Kecamatan Statistics Chart */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ height: { xs: 350, sm: 450, md: 600 } }}>
            <Bar data={kecamatanChartData} options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                title: {
                  ...chartOptions.plugins.title,
                  text: "Statistik per Kecamatan"
                }
              }
            }} />
          </Box>
        </Paper>
        <Paper>
        <GrafikVerifikasi />
        </Paper>
        <Paper>
          <GrafikRegisterUsulan />
        </Paper>
      </Paper>
    </Container>
  );
};

export default GrafikSeluruhKecamatan;